import React from 'react';
import FormModal from './Generic/FormModal';
import AchievementForm from './AchievementForm';
import { FloppyFill as  FloppyIcon } from './Generic/Icons';
import RoleSelection from './RoleSelection';
import { addClassNames } from '../Functions/Helper/Component';
import { bindOnChange } from '../Functions/Helper/OnChange';
import ProfessionalForm from './ProfessionForm';
import useModalPortal from '../Hooks/useModalPortal';
import LoadingScreen from './Generic/LoadingSpinner';
import { useProfessionSubmission } from '../Hooks/useProfessionSubmission';

function RefSpoofedAchievementForm(props) {
    const { modalDialogDOM } = useModalPortal();
    const container = React.useMemo(() => ({ current : modalDialogDOM }), [modalDialogDOM]);
    return <AchievementForm forceCallback formContainerRef={container} {...props}/>;
}

function useTitleComponent({ onChange, roleId, roleValue, setRoleFormData, newRole}) {
    const onRoleChange = React.useCallback((value) => {
        bindOnChange(onChange, "_mcb_role_value")(value)
    }, [onChange]);

    const TitleComponent = React.useCallback(({children, ..._props}) => {
        return (
            <div className="d-flex flex-grow-1 flex-row nowrap justify-content-between align-items-center gap-5">
                <div {..._props}><h5>{children}</h5></div>
                {!roleId && (
                    <div><RoleSelection
                        setFirstValue
                        CreateIfEmpty
                        value={roleValue}
                        onChange={onRoleChange}
                        onCreateOption={(mcb_roletitle) => {
                            setRoleFormData({ mcb_roletitle });
                            newRole();
                        }}
                        required={true}
                    /></div>
                )}
            </div>
        );
    }, [onRoleChange, roleId, roleValue, setRoleFormData, newRole]);

    return TitleComponent;
}

export default function AchievementModal({
    title, 
    actionName, 
    value: abcFormData, 
    onSubmit: onABCSubmit, 
    onChange: onABCChange,  
    onClose: onModalClose,
    newABC,
    newRole,
    roleId,
    setRoleId,
    classNames: propsClassNames, ...props
}) {
    const hasChanged = React.useRef(false);
    const {
        onProfessionSubmit, roleFormData, setRoleFormData,
        disableRoleSubmit, loading
    } = useProfessionSubmission(roleId, setRoleId);
    
    const onChange = React.useCallback((value) => {
        hasChanged.current = true;
        if (roleId) {
            setRoleFormData(value);
        } else {
            onABCChange(value);
        }
    }, [roleId, setRoleFormData, onABCChange]);

    const onSubmit= React.useCallback(async (value) => {
        if (roleId) {
            await onProfessionSubmit(value);
        } else if (typeof onABCSubmit === "function") {
            await onABCSubmit(value);
        }
        hasChanged.current = false;
        return true;
    }, [roleId, onProfessionSubmit, onABCSubmit]);
    
    const onClose = React.useCallback(() => {
        hasChanged.current = false;
        if (typeof onModalClose === "function") {
            onModalClose();
        }
    }, [onModalClose]);

  
    const classNames = React.useMemo(() => {
        const classNames = {
            modalTitleClassName: "btn-primary",
            modalDialogClassName: "modal-dialog-centered", 
            modalTriggerClassName: "border-0 fs-5 p-0 shadow-none text-decoration-underline",
            closeActionClassName: "btn icon-button"
        };
        Object.entries(propsClassNames || {}).forEach(([key, value]) => {
            classNames[key] = addClassNames(value, classNames[key]);
        });
        return classNames
    }, [propsClassNames]);
    
    const disableSubmit= React.useMemo(() => {
        if (roleId) {
            return disableRoleSubmit;
        } else {
            return !(
                abcFormData.skills?.length > 0
                && abcFormData.mcb_achievement?.length > 3
                && abcFormData.mcb_benefit?.length > 3
                && abcFormData.mcb_achievementstatement?.length > 3
            );
        }
    }, [roleId, disableRoleSubmit, abcFormData]);
    
    const TitleComponent = useTitleComponent({
        onChange, roleId, setRoleFormData, newRole,
        roleValue: abcFormData?._mcb_role_value,
    });
    

    const dynamicProps = React.useMemo(() => {
        const getProps = ([entity, Body, value, isNew, addNew, actionName]) => ({
            title: `${isNew? "Update" : "Add"} ${entity}`,
            actionName: actionName || <><FloppyIcon className="me-2"/>{isNew? "Save" : "Update"} {entity}</>,
            actionType: isNew? "Update" : "Save",
            BodyComponent: loading? LoadingScreen : Body,
            addNew, value
        });
        const args = (roleId? 
            ["Role", ProfessionalForm, roleFormData, !roleFormData?.mcb_roleid, newRole]
            : ["Achievement", RefSpoofedAchievementForm, abcFormData, !abcFormData?.mcb_abcid, newABC, actionName]
        );
        return getProps(args);
    }, [roleId, loading, roleFormData, abcFormData, newABC, newRole, actionName]);
    
    return (
        <FormModal
            TitleComponent={TitleComponent}
            classNames={classNames}
            onChange={onChange}
            onSubmit={onSubmit}
            onClose={onClose}
            quitConfirmation={hasChanged.current}
            disableSubmit={disableSubmit}
            {...dynamicProps}
            {...props}
        />
    );
}